import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Chip,
  Stack,
  Divider,
  Tooltip as MuiTooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  ArrowUpward,
  ArrowDownward,
  LocalShipping,
  ErrorOutline,
  AttachMoney,
} from "@mui/icons-material";

const TopSuppliersBySpend = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("chart");
  const [totalSpend, setTotalSpend] = useState(0);
  const [prevData, setPrevData] = useState(null);

  // Enhanced color palette with fallbacks
  const getColorPalette = () => {
    const defaultColors = [
      "#8884d8", // Purple
      "#82ca9d", // Green
      "#ffc658", // Yellow
      "#ff8042", // Orange
      "#0088FE", // Blue
      "#00C49F", // Teal
      "#FFBB28", // Gold
      "#FF5733", // Red-Orange
      "#9D5BFF", // Violet
      "#5B8CFF", // Light Blue
    ];

    try {
      // Try to get colors from theme, fallback to defaults if not available
      return [
        colors?.blueAccent?.[500] || defaultColors[0],
        colors?.greenAccent?.[500] || defaultColors[1],
        colors?.yellowAccent?.[500] || defaultColors[2],
        colors?.orangeAccet?.[500] || defaultColors[3],
        colors?.blueAccent?.[300] || defaultColors[4],
        colors?.greenAccent?.[300] || defaultColors[5],
        colors?.yellowAccent?.[300] || defaultColors[6],
        colors?.redAccent?.[500] || defaultColors[7],
        colors?.purpleAccent?.[500] || defaultColors[8],
        colors?.lightBlueAccent?.[500] || defaultColors[9],
      ];
    } catch {
      return defaultColors;
    }
  };

  // Configuration with enhanced color handling
  const CONFIG = {
    colors: {
      primary: getColorPalette(),
      cardBg: theme.palette.background.default,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      success: colors?.greenAccent?.[500] || "#4caf50",
      warning: colors?.orangeAccent?.[500] || "#ff9800",
      error: colors?.redAccent?.[500] || "#f44336",
      chartGrid: theme.palette.mode === "dark" ? "#424242" : "#e0e0e0",
    },
  };

  // Dummy data generation (fallback)
  const generateDummyData = () => {
    const supplierNames = [
      "BKD TRANSPORT AGENCY",
      "ANESH GUPTA",
      "HUSSAIN ALI",
      "M/S BISHAL ENTERPRISE",
      "BAHARUL ISLAM",
      "ASHIKUR RAHMAN",
      "ELIGA BEGUM",
      "MUKSIDUL ISLAM",
      "RAHUL MITRA",
      "MAA LOGISTICS",
    ];

    const dummyData = [];
    let total = 0;

    for (let i = 0; i < 10; i++) {
      const spend = Math.floor(Math.random() * 1000000) + 200000;
      const itemsPurchased = Math.floor(Math.random() * 50) + 5;
      const deliveryPerformance = Math.floor(Math.random() * 20) + 80; // 80-100%

      dummyData.push({
        supplier: supplierNames[i],
        spend: spend,
        itemsPurchased: itemsPurchased,
        deliveryPerformance: deliveryPerformance,
        share: 0,
      });

      total += spend;
    }

    dummyData.forEach((item) => {
      item.share = (item.spend / total) * 100;
    });

    dummyData.sort((a, b) => b.spend - a.spend);

    return {
      data: dummyData,
      total: total,
    };
  };

  const calculateStats = (data) => {
    if (!data || data.length === 0) return { total: 0, avgDelivery: 0 };
    const total = data.reduce((sum, item) => sum + item.spend, 0);
    const avgDelivery =
      data.reduce((sum, item) => sum + item.deliveryPerformance, 0) /
      data.length;
    return { total, avgDelivery };
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);
      setPrevData(data);

      // API call to get top suppliers
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/topTenSuppliersBySpend`,
        { cmpid, fromdate: "2020/01/01", todate: "2030/08/01" },
        { headers: { Authorization: token } }
      );

      // Check if API response is empty
      if (!response.data || Object.keys(response.data).length === 0) {
        throw new Error("API returned empty data");
      }

      // Transform API response to match our data structure
      const apiData = Object.values(response.data);
      let total = 0;

      const formattedData = apiData.map((item, index) => {
        const spend = parseFloat(item.Spend) || 0;
        total += spend;

        // Add simulated data for missing fields
        return {
          supplier: item.PartyName,
          spend: spend,
          itemsPurchased: Math.floor(Math.random() * 50) + 5, // Simulated
          deliveryPerformance: Math.floor(Math.random() * 20) + 80, // Simulated 80-100%
          share: 0, // Will be calculated below
        };
      });

      // Calculate share of total
      formattedData.forEach((item) => {
        item.share = (item.spend / total) * 100;
      });

      // Sort by spend
      formattedData.sort((a, b) => b.spend - a.spend);

      setData(formattedData);
      setTotalSpend(total);
    } catch (err) {
      console.error("Error fetching top suppliers data:", err);
      setError("Failed to load data. Using sample data.");
      const sampleData = generateDummyData();
      setData(sampleData.data);
      setTotalSpend(sampleData.total);
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "chart" ? "table" : "chart");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const exportToCSV = () => {
    const headers = [
      "Supplier",
      "Total Spend",
      "Items Purchased",
      "Delivery Performance",
      "Share of Total",
    ];
    const rows = data.map((item) => [
      `"${item.supplier}"`,
      item.spend,
      item.itemsPurchased,
      item.deliveryPerformance,
      item.share.toFixed(2),
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "top_suppliers_by_spend.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;

      const deliveryColor =
        dataItem.deliveryPerformance > 90
          ? CONFIG.colors.success
          : dataItem.deliveryPerformance > 80
          ? CONFIG.colors.warning
          : CONFIG.colors.error;

      return (
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            p: 2,
            minWidth: 180,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.supplier}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Total Spend:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(dataItem.spend)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Items Purchased:
              </Typography>
              <Typography variant="body2">{dataItem.itemsPurchased}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Delivery Performance:
              </Typography>
              <Typography variant="body2" sx={{ color: deliveryColor }}>
                {dataItem.deliveryPerformance}%
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Share of Total:
              </Typography>
              <Typography variant="body2">
                {dataItem.share.toFixed(1)}%
              </Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = calculateStats(data);
  const prevStats = prevData
    ? calculateStats(prevData)
    : { total: 0, avgDelivery: 0 };
  const spendChange = calculateChange(stats.total, prevStats.total);
  const deliveryChange = calculateChange(
    stats.avgDelivery,
    prevStats.avgDelivery
  );

  const top3Share =
    data.length >= 3
      ? data.slice(0, 3).reduce((sum, item) => sum + item.share, 0)
      : data.reduce((sum, item) => sum + item.share, 0);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 450,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: CONFIG.colors.primary[0],
                  width: 32,
                  height: 32,
                }}
              >
                <LocalShipping fontSize="small" />
              </Avatar>
              Top Suppliers by Spend
              <MuiTooltip title="Shows your highest spending suppliers with performance metrics">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last 12 months procurement data
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={
                viewMode === "chart" ? <TableChart /> : <BarChartIcon />
              }
              sx={{ textTransform: "none", color: CONFIG.colors.textSecondary, }}
            >
              {viewMode === "chart" ? "Table" : "Chart"}
            </Button>

            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{ textTransform: "none",
              color: CONFIG.colors.textSecondary,}}
            >
              Export  
            </Button>
          </Box>
        </Box>

        {isUsingSampleData && (
          <Box>
            <Chip
              label="No data available. Showing sample data."
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
            />
            <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
          </Box>
        )}
        

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={60} />
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "repeat(3, 1fr)"}
              gap={2}
            >
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Spend
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {formatCurrency(stats.total)}
                </Typography>
                {spendChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {spendChange > 0 ? (
                      <ArrowUpward
                        sx={{
                          fontSize: 14,
                          color: CONFIG.colors.error,
                        }}
                      />
                    ) : (
                      <ArrowDownward
                        sx={{
                          fontSize: 14,
                          color: CONFIG.colors.success,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          spendChange > 0
                            ? CONFIG.colors.error
                            : CONFIG.colors.success,
                        ml: 0.5,
                      }}
                    >
                      {Math.abs(spendChange).toFixed(1)}%
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Top 3 Suppliers Share
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {top3Share.toFixed(1)}%
                </Typography>
                <Typography variant="caption" mt={0.5}>
                  {data.length > 0 &&
                    `${data[0].supplier} (${data[0].share.toFixed(1)}%)`}
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Avg Delivery Performance
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  mt={0.5}
                  sx={{
                    color:
                      stats.avgDelivery > 90
                        ? CONFIG.colors.success
                        : stats.avgDelivery > 80
                        ? CONFIG.colors.warning
                        : CONFIG.colors.error,
                  }}
                >
                  {stats.avgDelivery.toFixed(1)}%
                </Typography>
                {deliveryChange !== 0 && (
                  <Box display="flex" alignItems="center" mt={0.5}>
                    {deliveryChange > 0 ? (
                      <ArrowUpward
                        sx={{
                          fontSize: 14,
                          color: CONFIG.colors.success,
                        }}
                      />
                    ) : (
                      <ArrowDownward
                        sx={{
                          fontSize: 14,
                          color: CONFIG.colors.error,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          deliveryChange > 0
                            ? CONFIG.colors.success
                            : CONFIG.colors.error,
                        ml: 0.5,
                      }}
                    >
                      {Math.abs(deliveryChange).toFixed(1)}%
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table Area */}
        <Box
          flex={1}
          minHeight={250}
          position="relative"
          mt={2}
          sx={{
            backgroundColor: CONFIG.colors.cardBg,
          }}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "chart" ? (
            <Box height="100%" p={2}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CONFIG.colors.chartGrid}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    name="Top Supplier by Spend"
                    tickFormatter={(value) =>
                      formatCurrency(value).replace("₹", "")
                    }
                    stroke={CONFIG.colors.textSecondary}
                  />
                  <YAxis
                    dataKey="supplier"
                    type="category"
                    width={100}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    stroke={CONFIG.colors.textSecondary}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="spend"
                    name="Total Spend"
                    radius={[0, 4, 4, 0]} // Rounded corners on the right side
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          CONFIG.colors.primary[
                            index % CONFIG.colors.primary.length
                          ]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Total Spend</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Delivery %</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => {
                    const deliveryColor =
                      row.deliveryPerformance > 90
                        ? CONFIG.colors.success
                        : row.deliveryPerformance > 80
                        ? CONFIG.colors.warning
                        : CONFIG.colors.error;

                    return (
                      <TableRow key={index} hover>
                        <TableCell>{row.supplier}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(row.spend)}
                        </TableCell>
                        <TableCell align="right">
                          {row.itemsPurchased}
                        </TableCell>
                        <TableCell align="right" sx={{ color: deliveryColor }}>
                          {row.deliveryPerformance}%
                        </TableCell>
                        <TableCell align="right">
                          {row.share.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TopSuppliersBySpend;